import React, { useEffect } from 'react';
import { Popconfirm, Switch } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import IconExclamationMark from '@/assets/icon-exclamation-mark.svg?react';
import { permissions } from '@/constants';
import { DeviceAllowedActions, ProactiveEngineStateEnum } from '@/constants/enums';
import SpinCentered from '@/comp/SpinCentered';
import { useStores } from '@/hooks/use-stores';
import ErrorModal from '@/comp/ErrorModal';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import { CustomThemeToken } from '@/theme';

import ToggleContainer from './ToggleContainer';

const { useToken } = theme;

export const PowerOnOffSwitch = observer(
  ({
    deviceId,
    forceDisable = false,
    stopPropagation = true,
  }: {
    deviceId: string;
    forceDisable?: boolean;
    stopPropagation?: boolean;
  }) => {
    const { devicesStore, stationStore, controlCenterStore, permissionStore, proactiveEngineStore } = useStores();
    const { t } = useTranslation();
    const { token } = useToken() as unknown as CustomThemeToken;

    useEffect(() => {
      controlCenterStore.updateDeviceStateOnMetricUpdate(deviceId);
    }, [stationStore.getDeviceById(deviceId)?.poweredOn]);

    const deviceMetadata = devicesStore.deviceMetadataFromDeviceId(deviceId);
    if (!deviceMetadata) {
      // Data not ready yet - will be auto-fixed when data will be loaded
      return;
    }
    const loading = controlCenterStore.isPendingPoweredOnStateChange.get(deviceId);
    const isProactiveEngineRunning = proactiveEngineStore.proactiveEngineState === ProactiveEngineStateEnum.Activated;
    const powerChangeSupportedAction =
      deviceMetadata.actions?.filter(action => Object.values(DeviceAllowedActions).includes(action)).length > 0;
    const isPowerChangeAllowed = permissionStore.isAuth(permissions.CONTROL_DEVICE);
    const isDisabled =
      forceDisable || isProactiveEngineRunning || !powerChangeSupportedAction || loading || !isPowerChangeAllowed;
    const poweredOnState = getPoweredOnState(stationStore.getDeviceById(deviceId)?.poweredOn);

    const onChangeSwitch = async () => {
      await controlCenterStore.toggleOnOff(deviceId);
    };

    const onClickWrapper = e => {
      stopPropagation && e.stopPropagation();
    };

    const getDisableReason = () => {
      if (forceDisable) return t('Changes can be made in Station Control Center only.');
      if (isProactiveEngineRunning) return t('Function is disabled since Engine Mode is Activated');
      if (!powerChangeSupportedAction)
        return t('Function is disabled since changing power state is not supported on this compressor.');
      if (loading) return t('Waiting for state to change.');
      if (!isPowerChangeAllowed) return t('You are not allowed to change power state for devices.');
    };

    function getPopConfirmTitle() {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '15px',
          }}
        >
          <IconExclamationMark width="40px" height="40px" />
          <div>
            {poweredOnState
              ? t('Turn compressor off verification', { name: deviceMetadata.name })
              : t('Turn compressor on verification', { name: deviceMetadata.name })}
          </div>
        </div>
      );
    }

    function getErroredDeviceName() {
      return stationStore.getDeviceById(controlCenterStore.erroredDeviceId)?.name;
    }

    return (
      <ToggleContainer
        switchState={poweredOnState}
        onClick={onClickWrapper}
        colorOn={token.colorBorderSecondary}
        colorOff={token.Ecoplant_Compressor.colorBGStopped}
        isDisabled={false}
        toggleColorOn={token.colorPrimary}
        toggleColorOff={token.colorError}
      >
        {loading && <SpinCentered className="spinner" />}
        <div className="switch-title">{t('OPERATION')}</div>
        <ConditionalToolTip condition={isDisabled} content={getDisableReason()} placement="bottom">
          <Popconfirm
            placement="top"
            title={getPopConfirmTitle()}
            onConfirm={() => onChangeSwitch()}
            okText={t('Yes')}
            cancelText={t('Cancel')}
            icon={null}
            overlayInnerStyle={{ width: '358px' }}
            disabled={isDisabled}
          >
            <div className="switch-options">
              <span
                style={loading ? { color: token.colorTextTertiary } : { color: token.colorText }}
                className="switch-value-text"
              >
                {poweredOnState ? t('Start') : t('Stop')}
              </span>
              <Switch size="small" checked={loading ? !poweredOnState : poweredOnState} disabled={isDisabled} />
            </div>
          </Popconfirm>
        </ConditionalToolTip>

        <ErrorModal
          condition={controlCenterStore.erroredDeviceId}
          errorText={t('CHANGE_COMPRESSOR_STATE_FAILED', { deviceName: getErroredDeviceName() })}
          resetCallback={controlCenterStore.resetError}
        />
      </ToggleContainer>
    );
  },
);
